import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CompareIcon from '@mui/icons-material/CompareArrows';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import GoogleTranslate from '../i18n/GoogleTranslate';
import GradePillSelect from './GradePillSelect';
import MobileMenu from './MobileMenu';
import MyListButton from './MyListButton';
import ThemeToggle from '../i18n/ThemeToggle';
import RecentSearchesDrawer from '../search/RecentSearchesDrawer';
import { useAppConfig } from '../../hooks/AppConfigContext';
import { useHeaderActions } from '../../hooks/HeaderActionsContext';
import { ColorMode } from '../../theme/buildTheme';
import {
  PILL_MIN_HEIGHT, PILL_RADIUS, pillBg, pillBgHover,
} from './headerPill';

interface HeaderProps {
  mode: ColorMode;
  onToggleMode: () => void;
}

/**
 * Global app header (Figma "pill" redesign). White surface everywhere; on the
 * results screen it also surfaces the active search (address + grade) and
 * Compare, which pages publish via HeaderActionsContext.
 */
const Header = ({ mode, onToggleMode }: HeaderProps) => {
  const { config } = useAppConfig();
  const { t } = useTranslation();
  const theme = useTheme();
  const [params] = useSearchParams();
  const {
    hasEdit, hasCompare, canCompare, editSearch, openCompare,
  } = useHeaderActions();
  const [menuOpen, setMenuOpen] = useState(false);

  // Desktop only: the address/grade/Compare controls. On phones the search
  // summary lives in ResultsMobileBar, so the header stays logo + controls.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Print trips the mobile breakpoint, which would drop the address/grade
  // summary. Treat print as desktop so the header still shows the search.
  const isPrinting = useMediaQuery('print');
  const address = params.get('address') || '';
  const showSearch = (isDesktop || isPrinting) && hasEdit && Boolean(address);
  const showCompare = isDesktop && hasCompare && canCompare;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        '@media print': { boxShadow: 'none' },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          justifyContent: 'space-between',
          gap: 2,
          minHeight: { xs: 56, sm: 64 },
          // Match the results toolbar's side padding so the header content lines
          // up with the Back / Filters row (and the gaps are equal both sides).
          px: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2.5}
          sx={{ minWidth: 0, flex: showSearch ? 1 : 'initial' }}
        >
          <Box
            component={RouterLink}
            to="/"
            aria-label={config.orgName || t('finder.app_title')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              flexShrink: 0,
            }}
          >
            {config.logoUrl ? (
              // White rounded container keeps the logo legible on any surface,
              // including dark mode where the header is a dark slate.
              <Box
                sx={{
                  display: 'flex',
                  bgcolor: 'common.white',
                  borderRadius: '10px',
                  p: 1,
                }}
              >
                <Box
                  component="img"
                  src={config.logoUrl}
                  alt={config.orgName || ''}
                  sx={{ height: 40, display: 'block' }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.95rem',
                  }}
                >
                  {(config.orgName || 'SF').slice(0, 2).toUpperCase()}
                </Box>
                <Typography
                  component="div"
                  className="notranslate"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    fontSize: '0.95rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {config.orgName || t('finder.app_title')}
                </Typography>
              </Box>
            )}
          </Box>

          {showSearch && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <ButtonBase
                onClick={editSearch}
                aria-label={t('finder.edit_search')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: PILL_MIN_HEIGHT,
                  px: 2,
                  borderRadius: PILL_RADIUS,
                  bgcolor: pillBg,
                  color: 'text.primary',
                  maxWidth: 320,
                  overflow: 'hidden',
                  flexShrink: 1,
                  minWidth: 0,
                  transition: 'background-color 150ms ease, color 150ms ease',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <Typography
                  noWrap
                  sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: '20px' }}
                >
                  {address}
                </Typography>
              </ButtonBase>
              <GradePillSelect requireGradeSelected />
            </Stack>
          )}
        </Stack>

        <Stack
          data-testid="header-actions"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexShrink: 0, '@media print': { display: 'none' } }}
        >
          {isDesktop ? (
            <>
              <RecentSearchesDrawer />
              <MyListButton />
              {showCompare && (
                <Button
                  onClick={openCompare}
                  startIcon={<CompareIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    minHeight: PILL_MIN_HEIGHT,
                    borderRadius: PILL_RADIUS,
                    px: 2,
                    color: 'text.primary',
                    bgcolor: pillBg,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: pillBgHover },
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {t('finder.compare')}
                  </Box>
                </Button>
              )}
              <GoogleTranslate />
              <ThemeToggle mode={mode} onToggle={onToggleMode} />
            </>
          ) : (
            <IconButton
              aria-label={menuOpen ? t('finder.close') : t('finder.open_menu')}
              onClick={() => setMenuOpen((prev) => !prev)}
              sx={{
                width: PILL_MIN_HEIGHT,
                height: PILL_MIN_HEIGHT,
                borderRadius: PILL_RADIUS,
                bgcolor: pillBg,
                color: 'text.primary',
                '&:hover': { bgcolor: pillBgHover },
              }}
            >
              {menuOpen
                ? <CloseIcon sx={{ fontSize: 20 }} />
                : <MenuIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          )}
        </Stack>
      </Toolbar>
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        mode={mode}
        onToggleMode={onToggleMode}
      />
    </AppBar>
  );
};

export default Header;
